import React, { Fragment, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Menu, Transition } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import { LANGUAGE_OPTIONS, SupportedLanguage } from './index'
import { useLanguage } from './useLanguage'

interface AppLanguagePickerProps {
  className?: string
  variant?: 'full' | 'icon'
}

const GlobeIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
  </svg>
)

const ChevronDownIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
  </svg>
)

export const AppLanguagePicker: React.FC<AppLanguagePickerProps> = ({
  className = '',
  variant = 'full',
}) => {
  const { t } = useTranslation()
  const { language, changeLanguage, isUpdating } = useLanguage()
  const current = LANGUAGE_OPTIONS.find((l) => l.code === language) ?? LANGUAGE_OPTIONS[0]

  const isIconOnly = variant === 'icon'
  const buttonRef = useRef<HTMLButtonElement>(null)

  return (
    <Menu as="div" className={`relative ${className}`}>
      {({ open }) => (
        <>
          <Menu.Button
            ref={buttonRef}
            aria-label={t('language.select')}
            className={
              isIconOnly
                ? 'flex items-center justify-center rounded-[50%] h-5 w-5 sm:h-8 sm:w-8 md:h-11 md:w-11 hover:bg-sky transition-colors'
                : 'flex items-center gap-2 px-3 py-1.5 rounded-md bg-white hover:bg-sky transition-colors text-sm font-medium text-primary border border-grey-lightest hover:border-grey-lighter shadow-sm'
            }
            disabled={isUpdating}
          >
            {isIconOnly ? (
              <GlobeIcon className="fill-primary hover:fill-primary-brand w-6 h-6 md:w-7 md:h-7" />
            ) : (
              <>
                <GlobeIcon className="fill-primary w-4 h-4" />
                <span>{current.nativeName}</span>
                <ChevronDownIcon className="fill-primary w-4 h-4" />
              </>
            )}
          </Menu.Button>
          <PortaledMenuItems
            open={open}
            buttonRef={buttonRef}
            language={language}
            changeLanguage={changeLanguage}
          />
        </>
      )}
    </Menu>
  )
}

interface PortaledMenuItemsProps {
  open: boolean
  buttonRef: React.RefObject<HTMLButtonElement>
  language: SupportedLanguage
  changeLanguage: (lang: SupportedLanguage) => Promise<void>
}

const PortaledMenuItems: React.FC<PortaledMenuItemsProps> = ({
  open,
  buttonRef,
  language,
  changeLanguage,
}) => {
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null)

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    setPosition({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    })
  }, [open, buttonRef])

  if (typeof document === 'undefined') return null

  return createPortal(
    <Transition
      as={Fragment}
      show={open}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <Menu.Items
        static
        style={position ? { top: position.top, right: position.right } : undefined}
        className="fixed w-44 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-[1000]"
      >
        <div className="py-1">
          {LANGUAGE_OPTIONS.map((opt) => {
            const selected = opt.code === language
            return (
              <Menu.Item key={opt.code}>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => changeLanguage(opt.code as SupportedLanguage)}
                    className={`${active ? 'bg-sky' : ''} ${
                      selected ? 'font-semibold text-primary-brand' : 'text-primary'
                    } group flex items-center gap-2 w-full px-3 py-2 text-sm`}
                  >
                    <span aria-hidden="true">{opt.flag}</span>
                    <span>{opt.nativeName}</span>
                    {selected && <span className="ml-auto text-primary-brand">✓</span>}
                  </button>
                )}
              </Menu.Item>
            )
          })}
        </div>
      </Menu.Items>
    </Transition>,
    document.body
  )
}
